import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  Email,
  Article,
  ShowChart,
  Send,
  ErrorOutline,
} from "@mui/icons-material";
import {
  apiFormPost,
  apiGet,
  apiPost,
  apiGetFile,
} from "../../services/genesisApi/httpsMethods";
import "../../assets/css/Text-General.css";
import "../../assets/css/Button.css";
import {
  DealTemplateCard,
  formatVersionOptionLabel,
  type DealTemplateVariantOption,
  type DealTemplateVersionItem,
} from "./DealTemplateCard";
import {
  fetchDealTemplateVariants,
  templateApiBasePath,
  tenPagerStateKey,
  tenPagerVariantQueryParam,
  withQueryParams,
  type DealTemplateVersionKey,
} from "./dealTemplateApi";

type TemplateCardConfig = {
  cardId: string;
  templateApiKey: DealTemplateVersionKey;
  label: string;
  icon: React.ReactNode;
};

type DealTemplateVersionsPayload = {
  total: number;
  items: DealTemplateVersionItem[];
};

function extractVersionItems(
  body: DealTemplateVersionsPayload | undefined,
): DealTemplateVersionItem[] {
  if (!body?.items || !Array.isArray(body.items)) return [];
  return body.items;
}

type DealTemplateUploadPayload = {
  template_key: string;
  variant_slug?: string;
  display_name?: string;
  current_blob_filename: string;
  version: number;
  blob_path: string;
  created_at: string;
  created_by_email: string;
  original_filename: string;
};

type LastUploadDisplay = Pick<
  DealTemplateUploadPayload,
  "current_blob_filename" | "created_at" | "created_by_email"
>;

function lastUploadFromPayload(
  payload: DealTemplateUploadPayload,
): LastUploadDisplay {
  return {
    current_blob_filename: payload.current_blob_filename,
    created_at: payload.created_at,
    created_by_email: payload.created_by_email,
  };
}

function revertFromPayload(
  payload: DealTemplateUploadPayload,
): LastUploadDisplay {
  return {
    current_blob_filename: payload.original_filename,
    created_at: payload.created_at,
    created_by_email: payload.created_by_email,
  };
}

function lastUploadFromVersionItem(
  item: DealTemplateVersionItem,
): LastUploadDisplay {
  return {
    current_blob_filename: item.original_filename,
    created_at: item.created_at,
    created_by_email: item.created_by_email,
  };
}

const DOCX_ACCEPT =
  ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX_ACCEPT =
  ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function fileAcceptForTemplate(templateApiKey: DealTemplateVersionKey): string {
  switch (templateApiKey) {
    case "term_sheet_email":
      return DOCX_ACCEPT;
    case "ten_pager":
    case "forward_rate_curves":
      return XLSX_ACCEPT;
    default: {
      const _exhaustive: never = templateApiKey;
      return _exhaustive;
    }
  }
}

function expectedExtensionLabel(
  templateApiKey: DealTemplateVersionKey,
): string {
  switch (templateApiKey) {
    case "term_sheet_email":
      return ".docx";
    case "ten_pager":
    case "forward_rate_curves":
      return ".xlsx";
    default: {
      const _exhaustive: never = templateApiKey;
      return _exhaustive;
    }
  }
}

function isAllowedTemplateFile(
  file: File,
  templateApiKey: DealTemplateVersionKey,
): boolean {
  const lower = file.name.toLowerCase();
  switch (templateApiKey) {
    case "term_sheet_email":
      return lower.endsWith(".docx");
    case "ten_pager":
    case "forward_rate_curves":
      return lower.endsWith(".xlsx");
    default: {
      const _exhaustive: never = templateApiKey;
      return _exhaustive;
    }
  }
}

/** Prior size was 56px; 3× for template cards — fill matches `.primary-button-solid` (Button.css) */
const iconSx = {
  fontSize: 168,
  width: 168,
  height: 168,
  color: "#1B7A63",
  display: "block",
} as const;

const templateCards: TemplateCardConfig[] = [
  {
    cardId: "term_sheet_email",
    templateApiKey: "term_sheet_email",
    label: "Short Form Term Sheet",
    icon: <Email sx={iconSx} />,
  },
  {
    cardId: "ten_pager",
    templateApiKey: "ten_pager",
    label: "Ten Pager",
    icon: <Article sx={iconSx} />,
  },
  {
    cardId: "forward_rate_curves",
    templateApiKey: "forward_rate_curves",
    label: "Forward Rate Curves",
    icon: <ShowChart sx={iconSx} />,
  },
];

const STATIC_CARD_STATE_KEYS = templateCards
  .filter((c) => c.templateApiKey !== "ten_pager")
  .map((c) => c.cardId);

function emptyCardState(): {
  items: DealTemplateVersionItem[];
  lastUpload: LastUploadDisplay | null;
  selectedVersionId: number | null;
} {
  return { items: [], lastUpload: null, selectedVersionId: null };
}

function cardStateFromVersions(
  body: DealTemplateVersionsPayload | undefined,
): {
  items: DealTemplateVersionItem[];
  lastUpload: LastUploadDisplay | null;
  selectedVersionId: number | null;
} {
  const items = extractVersionItems(body);
  const first = items[0];
  return {
    items,
    lastUpload: first ? lastUploadFromVersionItem(first) : null,
    selectedVersionId: first?.id ?? null,
  };
}

const AddTemplatesWrapper = () => {
  const [uploadingCardId, setUploadingCardId] = useState<string | null>(null);
  const [tenPagerVariants, setTenPagerVariants] = useState<
    DealTemplateVariantOption[]
  >([]);
  const [selectedTenPagerVariant, setSelectedTenPagerVariant] =
    useState<string>("default");

  const [cardStateById, setCardStateById] = useState<
    Record<
      string,
      {
        items: DealTemplateVersionItem[];
        lastUpload: LastUploadDisplay | null;
        selectedVersionId: number | null;
      }
    >
  >(() =>
    Object.fromEntries(
      STATIC_CARD_STATE_KEYS.map((cardId) => [cardId, emptyCardState()]),
    ),
  );

  const [revertModal, setRevertModal] = useState<{
    stateKey: string;
    templateApiKey: DealTemplateVersionKey;
    variantSlug?: string;
    versionId: number;
  } | null>(null);

  const [reinstateErrorModalOpen, setReinstateErrorModalOpen] = useState(false);

  const resolveCardStateKey = useCallback(
    (card: TemplateCardConfig, variant?: string): string => {
      if (card.templateApiKey === "ten_pager") {
        return tenPagerStateKey(variant ?? selectedTenPagerVariant);
      }
      return card.cardId;
    },
    [selectedTenPagerVariant],
  );

  const revertModalVersionItem = useMemo((): DealTemplateVersionItem | null => {
    if (!revertModal) return null;
    const items = cardStateById[revertModal.stateKey]?.items ?? [];
    return items.find((i) => i.id === revertModal.versionId) ?? null;
  }, [revertModal, cardStateById]);

  const handleRevertModalClose = useCallback(() => {
    setRevertModal(null);
  }, []);

  const handleDownloadRevertModalVersionFile = useCallback(async () => {
    if (!revertModal || !revertModalVersionItem) return;
    const apiBasePath = templateApiBasePath(revertModal.templateApiKey);
    const variantQuery = tenPagerVariantQueryParam(revertModal.variantSlug);
    const { version, original_filename: originalFilename } =
      revertModalVersionItem;
    const fallbackName =
      originalFilename ||
      `${revertModal.templateApiKey}-v${version}`;
    try {
      const result = await apiGetFile(
        `${apiBasePath}/versions/${version}/download`,
        variantQuery,
      );
      if (result.success && result.data) {
        const url = window.URL.createObjectURL(result.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fallbackName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Deal template download failed:", result.message);
      }
    } catch (error) {
      console.error(error);
    }
  }, [revertModal, revertModalVersionItem]);

  const handleReinstateErrorModalClose = useCallback(() => {
    setReinstateErrorModalOpen(false);
  }, []);

  const applyReinstateResponseToLastUpload = useCallback(
    (stateKey: string, payload: DealTemplateUploadPayload) => {
      const display = revertFromPayload(payload);
      setCardStateById((prev) => ({
        ...prev,
        [stateKey]: {
          ...prev[stateKey],
          lastUpload: display,
        },
      }));
    },
    [],
  );

  const fetchVersionsForKey = useCallback(
    async (
      stateKey: string,
      templateApiKey: DealTemplateVersionKey,
      variantSlug?: string,
    ) => {
      const apiBasePath = templateApiBasePath(templateApiKey);
      const variantQuery = tenPagerVariantQueryParam(variantSlug);
      const response = await apiGet<DealTemplateVersionsPayload>(
        `${apiBasePath}/versions`,
        variantQuery,
      );
      const payload: DealTemplateVersionsPayload | undefined =
        response.success && response.data !== undefined
          ? response.data
          : { total: 0, items: [] };
      setCardStateById((prev) => ({
        ...prev,
        [stateKey]: cardStateFromVersions(payload),
      }));
    },
    [],
  );

  const fetchStaticTemplateVersions = useCallback(async () => {
    await Promise.all(
      STATIC_CARD_STATE_KEYS.map(async (stateKey) => {
        await fetchVersionsForKey(
          stateKey,
          stateKey as DealTemplateVersionKey,
        );
      }),
    );
  }, [fetchVersionsForKey]);

  const loadTenPagerVariants = useCallback(async () => {
    const variants = await fetchDealTemplateVariants("ten_pager");
    setTenPagerVariants(variants);

    if (variants.length === 0) return;

    setSelectedTenPagerVariant((prev) =>
      variants.some((v) => v.slug === prev) ? prev : variants[0]!.slug,
    );

    setCardStateById((prev) => {
      const next = { ...prev };
      for (const variant of variants) {
        const key = tenPagerStateKey(variant.slug);
        if (!next[key]) next[key] = emptyCardState();
      }
      return next;
    });

    await Promise.all(
      variants.map((variant) =>
        fetchVersionsForKey(
          tenPagerStateKey(variant.slug),
          "ten_pager",
          variant.slug,
        ),
      ),
    );
  }, [fetchVersionsForKey]);

  const handleTenPagerVariantChange = useCallback(
    (variant: { slug: string; label: string }) => {
      setSelectedTenPagerVariant(variant.slug);
      setTenPagerVariants((prev) => {
        const existing = prev.find((v) => v.slug === variant.slug);
        if (existing) {
          return prev.map((v) =>
            v.slug === variant.slug ? { ...v, label: variant.label } : v,
          );
        }
        return [...prev, variant];
      });
      const stateKey = tenPagerStateKey(variant.slug);
      setCardStateById((prev) => {
        if (prev[stateKey]) return prev;
        return { ...prev, [stateKey]: emptyCardState() };
      });
      void fetchVersionsForKey(stateKey, "ten_pager", variant.slug);
    },
    [fetchVersionsForKey],
  );

  const selectedTenPagerVariantLabel = useMemo(
    () =>
      tenPagerVariants.find((v) => v.slug === selectedTenPagerVariant)?.label ??
      selectedTenPagerVariant,
    [tenPagerVariants, selectedTenPagerVariant],
  );

  useEffect(() => {
    void fetchStaticTemplateVersions();
    void loadTenPagerVariants();
  }, [fetchStaticTemplateVersions, loadTenPagerVariants]);

  const handleRevertModalConfirm = useCallback(async () => {
    if (!revertModal || !revertModalVersionItem) return;
    const apiBasePath = templateApiBasePath(revertModal.templateApiKey);
    const variantQuery = tenPagerVariantQueryParam(revertModal.variantSlug);
    const version = revertModalVersionItem.version;
    try {
      const response = await apiPost<DealTemplateUploadPayload>(
        withQueryParams(
          `${apiBasePath}/versions/${version}/reinstate`,
          variantQuery,
        ),
        { notes: "optional" },
      );
      if (response.success && response.data !== undefined) {
        applyReinstateResponseToLastUpload(revertModal.stateKey, response.data);
        setRevertModal(null);
      } else {
        setRevertModal(null);
        setReinstateErrorModalOpen(true);
      }
    } catch {
      setRevertModal(null);
      setReinstateErrorModalOpen(true);
    }
  }, [
    revertModal,
    revertModalVersionItem,
    applyReinstateResponseToLastUpload,
  ]);

  const processTemplateUpload = useCallback(
    async (
      file: File,
      card: TemplateCardConfig,
      variantSlug?: string,
    ) => {
      if (!isAllowedTemplateFile(file, card.templateApiKey)) {
        console.error(
          `[AddTemplates] Invalid file type for template key "${card.templateApiKey}": ` +
            `only ${expectedExtensionLabel(card.templateApiKey)} files are allowed. ` +
            `Received: "${file.name}"`,
        );
        return;
      }

      const stateKey = resolveCardStateKey(card, variantSlug);
      setUploadingCardId(stateKey);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const apiBasePath = templateApiBasePath(card.templateApiKey);
        const variantQuery = tenPagerVariantQueryParam(variantSlug);
        const response = await apiFormPost(
          withQueryParams(`${apiBasePath}/upload`, variantQuery),
          formData,
        );

        if (response.success) {
          const info = lastUploadFromPayload(
            response.data as DealTemplateUploadPayload,
          );
          const versionsResponse = await apiGet<DealTemplateVersionsPayload>(
            `${apiBasePath}/versions`,
            variantQuery,
          );
          const refreshed =
            versionsResponse.success && versionsResponse.data
              ? cardStateFromVersions(versionsResponse.data)
              : null;
          setCardStateById((prev) => ({
            ...prev,
            [stateKey]: {
              items: refreshed?.items ?? prev[stateKey]?.items ?? [],
              selectedVersionId:
                refreshed?.selectedVersionId ??
                prev[stateKey]?.selectedVersionId ??
                null,
              lastUpload: info,
            },
          }));
        }
      } finally {
        setUploadingCardId(null);
      }
    },
    [resolveCardStateKey],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        padding: "12px",
        justifyContent: "center",
        alignItems: "stretch",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {templateCards.map((card) => {
          const isTenPager = card.templateApiKey === "ten_pager";
          const activeVariant = isTenPager ? selectedTenPagerVariant : undefined;
          const stateKey = resolveCardStateKey(card, activeVariant);
          const state = cardStateById[stateKey] ?? emptyCardState();
          const apiBasePath = templateApiBasePath(card.templateApiKey);
          const apiQueryParams = tenPagerVariantQueryParam(activeVariant);
          return (
            <DealTemplateCard
              key={isTenPager ? `${card.cardId}-${activeVariant}` : card.cardId}
              cardId={card.cardId}
              label={card.label}
              icon={card.icon}
              apiBasePath={apiBasePath}
              apiQueryParams={apiQueryParams}
              columnCount={templateCards.length}
              variantOptions={isTenPager ? tenPagerVariants : undefined}
              selectedVariant={activeVariant}
              selectedVariantLabel={
                isTenPager ? selectedTenPagerVariantLabel : undefined
              }
              onVariantChange={
                isTenPager ? handleTenPagerVariantChange : undefined
              }
              versionCount={state.items.length}
              accept={fileAcceptForTemplate(card.templateApiKey)}
              supportedFormatLabel={expectedExtensionLabel(card.templateApiKey)}
              isUploading={uploadingCardId === stateKey}
              anyUploadInProgress={uploadingCardId !== null}
              lastUpload={state.lastUpload}
              versionItems={state.items}
              selectedVersionId={state.selectedVersionId}
              onVersionRevertRequest={(versionId) => {
                setRevertModal({
                  stateKey,
                  templateApiKey: card.templateApiKey,
                  variantSlug: activeVariant,
                  versionId,
                });
              }}
              isFileAllowed={(file) =>
                isAllowedTemplateFile(file, card.templateApiKey)
              }
              onUploadRequest={(file) => {
                void processTemplateUpload(file, card, activeVariant);
              }}
            />
          );
        })}
      </Box>

      <Dialog
        open={revertModal !== null}
        onClose={handleRevertModalClose}
        aria-labelledby="revert-template-dialog-title"
      >
        <DialogTitle
          id="revert-template-dialog-title"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            pt: 3,
            pb: 1,
          }}
        >
          <Send sx={{ fontSize: 40, color: "primary.main", mb: 1.5 }} />
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 3, px: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to revert to the selected file?
          </Typography>
          {revertModalVersionItem ? (
            <Typography
              variant="body2"
              sx={{
                wordBreak: "break-word",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {formatVersionOptionLabel(revertModalVersionItem)}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mt: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Button
                className="primary-button-clear"
                onClick={handleRevertModalClose}
                sx={{
                  height: "32px",
                  width: "max-content",
                  minWidth: "max-content",
                }}
              >
                Cancel
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                gap: 1,
              }}
            >
              <Button
                className="primary-button-solid"
                onClick={handleRevertModalConfirm}
                sx={{
                  height: "32px",
                  width: "max-content",
                  minWidth: "max-content",
                }}
              >
                Revert
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                gap: 1,
              }}
            >
              <Button
                className="primary-button-solid"
                onClick={() => void handleDownloadRevertModalVersionFile()}
                disabled={!revertModalVersionItem}
                sx={{
                  height: "32px",
                  width: "max-content",
                  minWidth: "max-content",
                }}
              >
                Download
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reinstateErrorModalOpen}
        onClose={handleReinstateErrorModalClose}
        aria-labelledby="reinstate-error-dialog-title"
      >
        <DialogTitle
          id="reinstate-error-dialog-title"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            pt: 3,
            pb: 1,
          }}
        >
          <ErrorOutline sx={{ fontSize: 40, color: "error.main", mb: 1.5 }} />
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 3, px: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This reversal failed. Please try again, or contact support if the
            problem continues.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mt: 3,
            }}
          >
            <Button
              className="primary-button-solid"
              onClick={handleReinstateErrorModalClose}
              sx={{
                height: "32px",
                width: "max-content",
                minWidth: "max-content",
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AddTemplatesWrapper;
